import Plugin from '../core/Plugin';

/**
 * BookmarkPlugin - Manages section bookmarks for content splitting
 * Allows users to add visual bookmarks that indicate where content should be split
 */
class BookmarkPlugin extends Plugin {
  constructor(editor, options = {}) {
    super(editor, options);
    this.name = 'Bookmark';
    this.bookmarkCounter = 0;
  }

  init() {
    super.init();
    // Setup will happen when methods are called - they check if richEditor exists
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    super.destroy();
  }

  getToolbarButtons() {
    return [
      {
        name: 'bookmarks',
        buttons: [
          { command: 'addBookmark', label: '🔖+', title: 'Add Bookmark' },
          { command: 'versionSplit', label: '📑 Split', title: 'Split by Bookmarks and Versions' },
        ],
      },
    ];
  }

  executeCommand(command) {
    if (command === 'addBookmark') {
      this.addBookmark();
    } else if (command === 'removeBookmark') {
      this.removeBookmarkAtCursor();
    } else if (command === 'versionSplit') {
      this.showVersionSplitDialog();
    }
  }

  /**
   * Auto-add bookmarks before demme-component-primary elements and h1 elements with blue backgrounds
   */
  autoAddBookmarksToH1() {
    if (!this.editor.richEditor || this.editor.mode !== 'rich') return;

    // Look for demme-component-primary elements (backend compatibility)
    const primaryComponents = this.editor.richEditor.querySelectorAll('.demme-component-primary');
    primaryComponents.forEach((component) => {
      // Add bookmark before the component if not already bookmarked
      if (!this.hasBookmarkBefore(component)) {
        this.addBookmarkBeforeElement(component);
      }
    });

    // Also check for h1 elements with blue background (original implementation)
    const h1Elements = this.editor.richEditor.querySelectorAll('h1');
    h1Elements.forEach((h1) => {
      // Check if h1 has blue background (inline style or computed)
      const bgColor = h1.style.backgroundColor || window.getComputedStyle(h1).backgroundColor;
      const isBlueBackground = this.isBlueColor(bgColor);

      // Add bookmark before h1 if blue background and not already bookmarked
      if (isBlueBackground && !this.hasBookmarkBefore(h1)) {
        this.addBookmarkBeforeElement(h1);
      }
    });

    // Attach event listeners to all existing bookmarks (including pre-existing ones from HTML)
    this.attachRemoveListenersToExistingBookmarks();
  }

  /**
   * Check if a color is blue-ish
   */
  isBlueColor(colorStr) {
    if (!colorStr) return false;

    // Handle common blue color formats
    const bluePatterns = [
      /blue/i,
      /rgb\s*\(\s*\d+\s*,\s*\d+\s*,\s*(2[0-4]\d|25[0-5])\s*\)/, // rgb with blue value 200-255
      /#[0-9a-f]{2}[0-9a-f]{2}[8-9a-f]{2}/i, // hex with high blue component (128-255)
    ];

    return bluePatterns.some((pattern) => pattern.test(colorStr));
  }

  /**
   * Check if element already has a bookmark after it
   */
  hasBookmarkAfter(element) {
    const next = element.nextElementSibling;
    return (
      next &&
      next.classList &&
      (next.classList.contains('quillkit-bookmark') || next.classList.contains('content-bookmark'))
    );
  }

  /**
   * Check if element already has a bookmark before it
   */
  hasBookmarkBefore(element) {
    const prev = element.previousElementSibling;
    return (
      prev &&
      prev.classList &&
      (prev.classList.contains('quillkit-bookmark') || prev.classList.contains('content-bookmark'))
    );
  }

  /**
   * Add a bookmark at the current cursor position
   */
  addBookmark() {
    if (this.editor.mode !== 'rich') {
      alert('Bookmarks can only be added in Rich Text mode');
      return;
    }

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const bookmark = this.createBookmarkElement();

    // Insert bookmark at cursor position
    range.insertNode(bookmark);

    // Move cursor after bookmark
    range.setStartAfter(bookmark);
    range.setEndAfter(bookmark);
    selection.removeAllRanges();
    selection.addRange(range);

    this.editor.syncToTextarea();
  }

  /**
   * Add a bookmark after a specific element
   */
  addBookmarkAfterElement(element) {
    const bookmark = this.createBookmarkElement();
    if (element.nextSibling) {
      element.parentNode.insertBefore(bookmark, element.nextSibling);
    } else {
      element.parentNode.appendChild(bookmark);
    }
  }

  /**
   * Add a bookmark before a specific element
   */
  addBookmarkBeforeElement(element) {
    const bookmark = this.createBookmarkElement();
    element.parentNode.insertBefore(bookmark, element);
  }

  /**
   * Create a bookmark element
   */
  createBookmarkElement() {
    const bookmark = document.createElement('div');
    // Use both classes for compatibility: content-bookmark (backend) and quillkit-bookmark (frontend)
    bookmark.className = 'content-bookmark quillkit-bookmark';
    bookmark.contentEditable = 'false';
    bookmark.dataset.bookmark = 'true'; // Backend format
    bookmark.dataset.bookmarkId = `bookmark-${++this.bookmarkCounter}`; // Keep for internal tracking

    const label = document.createElement('span');
    label.className = 'quillkit-bookmark-label';
    label.textContent = '📑 Content Split'; // Match backend label

    const removeBtn = document.createElement('button');
    removeBtn.className = 'quillkit-bookmark-remove';
    removeBtn.textContent = '×';
    removeBtn.title = 'Remove bookmark';
    removeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      bookmark.remove();
      this.editor.syncToTextarea();
    });

    bookmark.appendChild(label);
    bookmark.appendChild(removeBtn);

    return bookmark;
  }

  /**
   * Attach event listeners to existing bookmark remove buttons
   * This is needed for bookmarks loaded from HTML
   */
  attachRemoveListenersToExistingBookmarks() {
    if (!this.editor.richEditor || this.editor.mode !== 'rich') return;

    const bookmarks = this.editor.richEditor.querySelectorAll(
      '.quillkit-bookmark, .content-bookmark'
    );
    bookmarks.forEach((bookmark) => {
      const removeBtn = bookmark.querySelector('.quillkit-bookmark-remove');
      if (removeBtn) {
        // Check if listener already attached by checking a data attribute
        if (!removeBtn.dataset.listenerAttached) {
          removeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            bookmark.remove();
            this.editor.syncToTextarea();
          });
          // Mark that we've attached the listener
          removeBtn.dataset.listenerAttached = 'true';
        }
      }
    });
  }

  /**
   * Remove bookmark at cursor position
   */
  removeBookmarkAtCursor() {
    if (this.editor.mode !== 'rich') return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    let node = range.startContainer;

    // Find the closest bookmark element
    while (node && node !== this.editor.richEditor) {
      if (
        node.classList &&
        (node.classList.contains('quillkit-bookmark') || node.classList.contains('content-bookmark'))
      ) {
        node.remove();
        this.editor.syncToTextarea();
        return;
      }
      node = node.parentNode;
    }

    alert('No bookmark found at cursor position');
  }

  /**
   * Setup mutation observer to maintain bookmarks
   */
  setupMutationObserver() {
    if (!this.editor.richEditor) return;

    this.observer = new MutationObserver(() => {
      // Re-apply bookmark styling if needed
      const bookmarks = this.editor.richEditor.querySelectorAll(
        '.quillkit-bookmark, .content-bookmark'
      );
      bookmarks.forEach((bookmark) => {
        bookmark.contentEditable = 'false';
      });

      // Attach event listeners to any bookmarks that don't have them
      this.attachRemoveListenersToExistingBookmarks();
    });

    this.observer.observe(this.editor.richEditor, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Show version split dialog
   */
  showVersionSplitDialog() {
    const bookmarks = this.getBookmarks();

    if (bookmarks.length === 0) {
      alert('No bookmarks found. Add bookmarks to split content into sections.');
      return;
    }

    // Get content sections
    const sections = this.getSections();

    // For now, show a simple dialog with the sections
    this.displaySplitResults(sections);
  }

  /**
   * Get all bookmarks in the editor
   */
  getBookmarks() {
    if (this.editor.mode !== 'rich') return [];
    return Array.from(
      this.editor.richEditor.querySelectorAll('.quillkit-bookmark, .content-bookmark')
    );
  }

  /**
   * Get content sections based on bookmarks
   */
  getSections() {
    const sections = [];
    const bookmarks = this.getBookmarks();

    if (bookmarks.length === 0) return sections;

    // Get all children of richEditor
    const allNodes = Array.from(this.editor.richEditor.childNodes);

    let currentSection = {
      bookmark: null,
      content: [],
    };

    allNodes.forEach((node) => {
      if (
        node.classList &&
        (node.classList.contains('quillkit-bookmark') || node.classList.contains('content-bookmark'))
      ) {
        // Save previous section if it has content
        if (currentSection.content.length > 0) {
          sections.push(currentSection);
        }
        // Start new section
        currentSection = {
          bookmark: node,
          bookmarkId: node.dataset.bookmarkId,
          label: node.querySelector('.quillkit-bookmark-label')?.textContent || 'Section',
          content: [],
        };
      } else {
        currentSection.content.push(node);
      }
    });

    // Add last section
    if (currentSection.content.length > 0) {
      sections.push(currentSection);
    }

    return sections;
  }

  /**
   * Display split results
   */
  displaySplitResults(sections) {
    // Create modal
    const overlay = document.createElement('div');
    overlay.className = 'quillkit-dialog-overlay';

    const modal = document.createElement('div');
    modal.className = 'quillkit-dialog quillkit-bookmark-split-dialog';

    // Header
    const header = document.createElement('div');
    header.className = 'quillkit-dialog-header';
    header.textContent = 'Content Split Results';
    modal.appendChild(header);

    // Body
    const body = document.createElement('div');
    body.className = 'quillkit-dialog-body';

    const info = document.createElement('div');
    info.className = 'quillkit-bookmark-split-info';
    info.innerHTML = `
      <p><strong>Versions created:</strong> ${sections.length}</p>
    `;
    body.appendChild(info);

    // Create section previews
    const sectionsContainer = document.createElement('div');
    sectionsContainer.className = 'quillkit-bookmark-sections';

    sections.forEach((section, index) => {
      const sectionDiv = document.createElement('div');
      sectionDiv.className = 'quillkit-bookmark-section';

      const sectionHeader = document.createElement('div');
      sectionHeader.className = 'quillkit-bookmark-section-header';
      sectionHeader.textContent = section.label || `Version ${index + 1}`;

      const sectionContent = document.createElement('div');
      sectionContent.className = 'quillkit-bookmark-section-content';

      // Clone content nodes for preview
      section.content.forEach((node) => {
        const clone = node.cloneNode(true);
        sectionContent.appendChild(clone);
      });

      const copyBtn = document.createElement('button');
      copyBtn.className = 'quillkit-editor-button';
      copyBtn.textContent = 'Copy HTML';
      copyBtn.addEventListener('click', () => {
        const html = sectionContent.innerHTML;
        navigator.clipboard.writeText(html).then(() => {
          copyBtn.textContent = 'Copied!';
          setTimeout(() => {
            copyBtn.textContent = 'Copy HTML';
          }, 2000);
        });
      });

      sectionDiv.appendChild(sectionHeader);
      sectionDiv.appendChild(sectionContent);
      sectionDiv.appendChild(copyBtn);
      sectionsContainer.appendChild(sectionDiv);
    });

    body.appendChild(sectionsContainer);
    modal.appendChild(body);

    // Footer
    const footer = document.createElement('div');
    footer.className = 'quillkit-dialog-footer';

    // Close modal function with cleanup
    const closeModal = () => {
      // Remove event listeners by removing elements
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      if (modal.parentNode) {
        modal.parentNode.removeChild(modal);
      }
    };

    const closeBtn = document.createElement('button');
    closeBtn.className = 'quillkit-editor-button';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', closeModal);

    footer.appendChild(closeBtn);
    modal.appendChild(footer);

    // Add to DOM
    document.body.appendChild(overlay);
    document.body.appendChild(modal);

    // Close on overlay click
    overlay.addEventListener('click', closeModal);
  }

  getMetadata() {
    return {
      name: 'Bookmark',
      version: '1.0.0',
      description: 'Manages section bookmarks for content splitting',
      author: 'AtlaraFirm',
    };
  }
}

export default BookmarkPlugin;
